import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getFlopValue, getGetStatFldChange, getInflowCalculations, getReserPrevChange, getSRCables } from "../../../services/calculations/calculation-input/well.service";
import { FlopPayload, inflowOutput } from "../../model/calculations/fluid";
import { cableResponse, Tab2Input } from "../../model/calculations/calculation";

interface WellState {
  loading: boolean;
  error: string | undefined;
  tab2Input: Tab2Input | null;
  settingDepthTypeVal: number | null;
  inflowOutput: inflowOutput | null;
  cableDetails: cableResponse | null;
}

export const initialState: WellState = {
  tab2Input: null,
  settingDepthTypeVal: null,
  loading: false,
  error: undefined,
  inflowOutput: null,
  cableDetails: null,
};
// Async Thunks
export const fetchFlopValue = createAsyncThunk(
  "well/fetchFlopValue",
  async (payload: FlopPayload) => {
    try {
      return await getFlopValue(payload);
    } catch (err: any) {
      throw Error("Error fetching Flop Value " + err);
    }
  }
);

export const fetchReservoirPressureChange = createAsyncThunk(
  "well/fetchReservoirPressureChange",
  async (payload: any, { rejectWithValue }) => {
    try {
      return await getReserPrevChange(payload);
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchStatFldChange = createAsyncThunk(
  "well/fetchStatFldChange",
  async (payload: any, { rejectWithValue }) => {
    try {
      return await getGetStatFldChange(payload);
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchInflowCalculations = createAsyncThunk(
  "well/fetchInflowCalculations",
  async (payload: any, { rejectWithValue }) => {
    try {
      return await getInflowCalculations(payload);
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchSRCables = createAsyncThunk(
  "well/fetchSRCables",
  async (payload: any, { rejectWithValue }) => {
    try {
      return await getSRCables(payload);
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);
const wellSlice = createSlice({
  name: "well",
  initialState,
  reducers: {
    resetWellDetails: () => ({
      ...initialState
    }),
    setWellTabDetails: (state, action) => {
      state.tab2Input = action?.payload;
    },
    setTubingPressure: (state, action: PayloadAction<number>) => {
      if (state?.tab2Input) {
        state.tab2Input = {
          ...state.tab2Input,
          tubingPressure: action?.payload
        };
      }
    },
    setCasingPressure: (state, action: PayloadAction<number>) => {
      if (state?.tab2Input) {
        state.tab2Input = {
          ...state.tab2Input,
          casingPressure: action?.payload
        };
      }
    },
    setInflowTypeLstVal: (state, action: PayloadAction<string>) => {
      if (state?.tab2Input) {
        state.tab2Input = {
          ...state.tab2Input,
          inflowTypeLstVal: action?.payload
        };
      }
    },
    setProductivityIndex: (state, action: PayloadAction<number>) => {
      if (state?.tab2Input) {
        state.tab2Input = {
          ...state.tab2Input,
          piVal: action?.payload
        };
      }
    },
    setStaticBottomHolePressurePI: (state, action: PayloadAction<number | null>) => {
      if (state?.tab2Input) {
        state.tab2Input = {
          ...state.tab2Input,
          reservoirPressure: action?.payload
        };
      }
    },
    setStaticFluidLevelFromSurfacePI: (state: any, action: PayloadAction<number>) => {
      state.tab2Input = {
        ...state.tab2Input,
        staticFluidLvl: action?.payload
      };
    },
    setStaticFluidLevelFromSurfacePI2: (state: any, action: PayloadAction<number>) => {
      state.tab2Input = {
        ...state.tab2Input,
        staticFluidLvl: action?.payload
      };
    },
    setDesiredProductionRate: (state, action: PayloadAction<number>) => {
      if (state?.tab2Input) {
        state.tab2Input = {
          ...state.tab2Input,
          productionRate: action?.payload
        };
      }
    },
    setStaticBottomHolePressureP: (state, action: PayloadAction<number>) => {
      if (state?.tab2Input) {
        state.tab2Input = {
          ...state.tab2Input,
          sbhpForPressre: action?.payload
        };
      }
    },
    setFlowingBottomHolePressureP: (state, action: PayloadAction<number>) => {
      if (state?.tab2Input) {
        state.tab2Input = {
          ...state.tab2Input,
          intakePressure: action?.payload
        };
      }
    },
    setTestFlowRate: (state, action: PayloadAction<number>) => {
      if (state?.tab2Input) {
        state.tab2Input = {
          ...state.tab2Input,
          flowRate: action?.payload
        };
      }
    },
    setStaticFluidLevelFromSurfaceHead: (state, action: PayloadAction<number>) => {
      if (state?.tab2Input) {
        state.tab2Input = {
          ...state.tab2Input,
          staticFluidLvlForHd: action?.payload
        };
      }
    },
    setProducingFluidLevelFromSurfaceHead: (state, action: PayloadAction<number>) => {
      if (state?.tab2Input) {
        state.tab2Input = {
          ...state.tab2Input,
          prdcngFluidLvl: action?.payload
        };
      }
    },
    setTestFlowRateHead: (state, action: PayloadAction<number>) => {
      if (state?.tab2Input) {
        state.tab2Input = {
          ...state.tab2Input,
          flowRateForHd: action?.payload
        };
      }
    },
    setSettingDepthReference: (state, action: PayloadAction<number>) => {
      state.settingDepthTypeVal = action.payload;
    },
    setSettingDepth: (state, action: PayloadAction<number>) => {
      if (state?.tab2Input) {
        state.tab2Input = {
          ...state.tab2Input,
          pumpSetting: action?.payload
        };
      }
    },
    setPumpSettingVerticalDepth: (state, action: PayloadAction<number>) => {
      if (state?.tab2Input) {
        state.tab2Input = {
          ...state.tab2Input,
          tvd: action?.payload
        };
      }
    },
    setReservoirDatum: (state, action: PayloadAction<number>) => {
      if (state?.tab2Input) {
        state.tab2Input = {
          ...state.tab2Input,
          perforation: action?.payload
        };
      }
    },
    setFlopValue: (state, action: PayloadAction<number | null>) => {
      if (state?.tab2Input) {
        state.tab2Input = {
          ...state.tab2Input,
          flop: action?.payload
        };
      }
    },
    setHiddenHeadPIIPType: (state, action: PayloadAction<string>) => {
      if (state?.tab2Input) {
        state.tab2Input = {
          ...state.tab2Input,
          hiddenPresHeadPIIPType: action?.payload
        };
      }
    },
    setInflowCalculations: (state, action: PayloadAction<any>) => {
      state.inflowOutput = action.payload;
    },
  }, extraReducers: (builder) => {
    // FLOP Value
    // builder
    //   .addCase(fetchFlopValue.pending, (state) => {
    //     state.loading = true;
    //     state.error = "";
    //   })
    //   .addCase(fetchFlopValue.fulfilled, (state, action) => {
    //     state.loading = false;
    //     state.flopVal = action.payload ?? null;
    //   })
    //   .addCase(fetchFlopValue.rejected, (state, action) => {
    //     state.loading = false;
    //     state.error = action.payload as string;
    //   });

    // Reservoir Pressure Change
    // builder
    //   .addCase(fetchReservoirPressureChange.pending, (state) => {
    //     state.loading = true;
    //     state.error = "";
    //   })
    //   .addCase(fetchReservoirPressureChange.fulfilled, (state, action) => {
    //     state.loading = false;
    //     state.staticFluidLevelFromSurfacePIVal = action.payload ?? null;
    //   })
    //   .addCase(fetchReservoirPressureChange.rejected, (state, action) => {
    //     state.loading = false;
    //     state.error = action.payload as string;
    //   });

    // Static Field Change
    // builder
    //   .addCase(fetchStatFldChange.pending, (state) => {
    //     state.loading = true;
    //     state.error = "";
    //   })
    //   .addCase(fetchStatFldChange.fulfilled, (state, action) => {
    //     state.loading = false;
    //     state.staticBottomHolePressurePIVal = action.payload ?? null;
    //   })
    //   .addCase(fetchStatFldChange.rejected, (state, action) => {
    //     state.loading = false;
    //     state.error = action.payload as string;
    //   });

    // Inflow Calculations
    builder
      .addCase(fetchInflowCalculations.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(fetchInflowCalculations.fulfilled, (state, action) => {
        state.loading = false;
        state.inflowOutput = action.payload ?? null;
      })
      .addCase(fetchInflowCalculations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    // Cable Details
    builder
      .addCase(fetchSRCables.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(fetchSRCables.fulfilled, (state, action) => {
        state.loading = false;
        state.cableDetails = action.payload ?? null;
      })
      .addCase(fetchSRCables.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  resetWellDetails,
  setWellTabDetails,
  setTubingPressure,
  setCasingPressure,
  setInflowTypeLstVal,
  setProductivityIndex,
  setStaticBottomHolePressurePI,
  setStaticFluidLevelFromSurfacePI,
  setStaticFluidLevelFromSurfacePI2,
  setDesiredProductionRate,
  setStaticBottomHolePressureP,
  setFlowingBottomHolePressureP,
  setTestFlowRate,
  setStaticFluidLevelFromSurfaceHead,
  setProducingFluidLevelFromSurfaceHead,
  setTestFlowRateHead,
  setSettingDepthReference,
  setSettingDepth,
  setPumpSettingVerticalDepth,
  setReservoirDatum,
  setFlopValue,
  setHiddenHeadPIIPType,
  setInflowCalculations,
} = wellSlice.actions;

export default wellSlice.reducer;
